import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  Chip,
  Alert,
  Tooltip,
  alpha,
  useTheme,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Home as HomeIcon,
  LocalOffer as OfferIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  ShoppingBag as ShoppingBagIcon,
  FiberManualRecord as DotIcon,
  Visibility as PreviewIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Edit as EditIcon,
  Badge as BadgeIcon
} from '@mui/icons-material';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';
import { DisplaySettings as DisplaySettingsType } from '../../../types/storeSettings';

// ========== Types ==========
interface MainMenuItem {
  id: string;
  title: string;
  icon: string;
  path: string;
  enabled: boolean;
  badge?: string;
  color?: string;
}

interface BottomMenuItem {
  id: string;
  title: string;
  icon: string;
  path: string;
  enabled: boolean;
}

// ========== Defaults ==========
const defaultMainMenuItems: MainMenuItem[] = [
  { id: 'home', title: 'الرئيسية', icon: 'home', path: '/', enabled: true, color: '#0d51d6' },
  { id: 'offers', title: 'العروض الخاصة', icon: 'local_offer', path: '/offers', enabled: true, color: '#f44336', badge: 'جديد' },
  { id: 'bestsellers', title: 'الأكثر مبيعاً', icon: 'trending_up', path: '/bestsellers', enabled: true, color: '#4caf50' },
  { id: 'featured', title: 'المنتجات المميزة', icon: 'star', path: '/featured', enabled: true, color: '#ff9800' }
];

const defaultBottomMenuItems: BottomMenuItem[] = [
  { id: 'about', title: 'حول المتجر', icon: 'info', path: '/about', enabled: true },
  { id: 'contact', title: 'اتصل بنا', icon: 'phone', path: '/contact', enabled: true },
  { id: 'profile', title: 'حسابي', icon: 'person', path: '/profile', enabled: true }
];

const iconOptions = [
  { value: 'home', label: '🏠 الرئيسية' },
  { value: 'local_offer', label: '🏷️ العروض' },
  { value: 'trending_up', label: '📈 الأكثر مبيعاً' },
  { value: 'star', label: '⭐ المميزة' },
  { value: 'info', label: 'ℹ️ حول المتجر' },
  { value: 'phone', label: '📞 اتصل بنا' },
  { value: 'person', label: '👤 حسابي' },
  { value: 'category', label: '📂 الأقسام' },
  { value: 'shopping_bag', label: '🛍️ التسوق' },
];

const getIconComponent = (iconName: string, color?: string, size?: 'small' | 'medium') => {
  const sx = { fontSize: size === 'small' ? '1rem' : '1.2rem', color: color || 'inherit' };
  switch (iconName) {
    case 'home': return <HomeIcon sx={sx} />;
    case 'local_offer': case 'offer': return <OfferIcon sx={sx} />;
    case 'trending_up': case 'trending': return <TrendingUpIcon sx={sx} />;
    case 'star': case 'featured': return <StarIcon sx={sx} />;
    case 'info': case 'about': return <InfoIcon sx={sx} />;
    case 'phone': case 'contact': return <PhoneIcon sx={sx} />;
    case 'person': case 'profile': return <PersonIcon sx={sx} />;
    case 'category': return <CategoryIcon sx={sx} />;
    case 'shopping_bag': return <ShoppingBagIcon sx={sx} />;
    default: return <DotIcon sx={{ fontSize: 10, color: color || 'inherit' }} />;
  }
};

// ========== Live Preview Component ==========
const MobileSidebarPreview: React.FC<{ mainItems: MainMenuItem[]; bottomItems: BottomMenuItem[]; storeName: string }> = ({
  mainItems, bottomItems, storeName
}) => {
  const theme = useTheme();
  const enabledMain = mainItems.filter(i => i.enabled);
  const enabledBottom = bottomItems.filter(i => i.enabled);

  return (
    <Box
      sx={{
        width: 240,
        height: 500,
        borderRadius: 3,
        overflow: 'hidden',
        border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        fontFamily: 'sans-serif'
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 1.5,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: '#fff',
        textAlign: 'center'
      }}>
        <Avatar sx={{ width: 36, height: 36, mx: 'auto', mb: 0.5, bgcolor: alpha('#fff', 0.25), fontSize: '0.9rem', fontWeight: 'bold' }}>
          {storeName?.charAt(0) || 'م'}
        </Avatar>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', lineHeight: 1 }}>{storeName || 'المتجر'}</Typography>
        <Typography sx={{ fontSize: '0.6rem', opacity: 0.85 }}>أفضل المنتجات</Typography>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
        {/* Main menu items */}
        {enabledMain.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.8,
              borderLeft: `3px solid ${item.color || theme.palette.primary.main}`,
              mx: 0.5,
              borderRadius: '0 4px 4px 0',
              mb: 0.5,
              '&:hover': { bgcolor: alpha(item.color || theme.palette.primary.main, 0.05) }
            }}
          >
            {getIconComponent(item.icon, item.color || theme.palette.primary.main, 'small')}
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, flex: 1, color: '#333' }}>{item.title}</Typography>
            {item.badge && (
              <Box sx={{
                bgcolor: '#f44336', color: '#fff', borderRadius: 1,
                px: 0.5, py: 0.1, fontSize: '0.55rem', fontWeight: 'bold'
              }}>
                {item.badge}
              </Box>
            )}
          </Box>
        ))}

        <Divider sx={{ my: 0.5, mx: 1, opacity: 0.4 }} />

        {/* Categories label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5 }}>
          <CategoryIcon sx={{ fontSize: '0.8rem', color: theme.palette.info.main }} />
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            الأقسام
          </Typography>
        </Box>

        {/* Sample category */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.6, bgcolor: alpha(theme.palette.primary.main, 0.05), borderLeft: `4px solid ${theme.palette.primary.main}` }}>
          <Avatar sx={{ width: 18, height: 18, bgcolor: alpha(theme.palette.primary.main, 0.15), fontSize: '0.6rem' }}>أ</Avatar>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: theme.palette.primary.main }}>قسم المنتجات</Typography>
        </Box>

        <Divider sx={{ my: 0.5, mx: 1, opacity: 0.4 }} />

        {/* Bottom menu items */}
        {enabledBottom.map((item) => (
          <Box
            key={item.id}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.7 }}
          >
            {getIconComponent(item.icon, '#888', 'small')}
            <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>{item.title}</Typography>
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 1, bgcolor: alpha('#f5f5f5', 0.8), borderTop: '1px solid #eee', textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.55rem', color: '#aaa' }}>© 2024 {storeName || 'المتجر'}</Typography>
      </Box>
    </Box>
  );
};

// ========== Menu Item Row ==========
const MainMenuItemRow: React.FC<{
  item: MainMenuItem;
  index: number;
  total: number;
  onChange: (id: string, field: string, value: any) => void;
  onToggle: (id: string, enabled: boolean) => void;
  onMove: (index: number, dir: 'up' | 'down') => void;
  onDelete: (id: string) => void;
}> = ({ item, index, total, onChange, onToggle, onMove, onDelete }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${item.enabled ? alpha(item.color || theme.palette.primary.main, 0.3) : '#e0e0e0'}`,
        borderRadius: 2,
        p: 2,
        mb: 1.5,
        bgcolor: item.enabled ? alpha(item.color || theme.palette.primary.main, 0.03) : '#fafafa',
        opacity: item.enabled ? 1 : 0.65,
        transition: 'all 0.2s ease'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {/* Color swatch + icon preview */}
        <Avatar sx={{ width: 32, height: 32, bgcolor: item.color || theme.palette.primary.main }}>
          {getIconComponent(item.icon, '#fff', 'small')}
        </Avatar>

        <Typography fontWeight={600} sx={{ flex: 1, fontSize: '0.9rem' }}>{item.title || 'عنصر جديد'}</Typography>

        {item.badge && <Chip label={item.badge} size="small" color="error" sx={{ height: 20, fontSize: '0.65rem' }} />}

        {/* Controls */}
        <Tooltip title="نقل لأعلى"><span>
          <IconButton size="small" disabled={index === 0} onClick={() => onMove(index, 'up')}><UpIcon fontSize="small" /></IconButton>
        </span></Tooltip>
        <Tooltip title="نقل لأسفل"><span>
          <IconButton size="small" disabled={index === total - 1} onClick={() => onMove(index, 'down')}><DownIcon fontSize="small" /></IconButton>
        </span></Tooltip>
        <Switch
          size="small"
          checked={item.enabled}
          onChange={(e) => onToggle(item.id, e.target.checked)}
          color="primary"
        />
        <Tooltip title="حذف العنصر">
          <IconButton size="small" color="error" onClick={() => onDelete(item.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={3}>
          <TextField
            size="small"
            fullWidth
            label="العنوان"
            value={item.title}
            disabled={!item.enabled}
            onChange={(e) => onChange(item.id, 'title', e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel>الأيقونة</InputLabel>
            <Select
              value={item.icon}
              label="الأيقونة"
              disabled={!item.enabled}
              onChange={(e) => onChange(item.id, 'icon', e.target.value)}
            >
              {iconOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField
            size="small"
            fullWidth
            label="اللون"
            value={item.color || ''}
            disabled={!item.enabled}
            placeholder="#0d51d6"
            onChange={(e) => onChange(item.id, 'color', e.target.value)}
            InputProps={{
              endAdornment: item.color ? (
                <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: item.color, border: '1px solid #ccc', flexShrink: 0 }} />
              ) : null
            }}
          />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField
            size="small"
            fullWidth
            label="الرابط"
            value={item.path}
            disabled={!item.enabled}
            onChange={(e) => onChange(item.id, 'path', e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={2.5}>
          <TextField
            size="small"
            fullWidth
            label="شارة (اختياري)"
            value={item.badge || ''}
            disabled={!item.enabled}
            placeholder="جديد / خصم"
            onChange={(e) => onChange(item.id, 'badge', e.target.value || undefined)}
            InputProps={{ startAdornment: <BadgeIcon sx={{ fontSize: '1rem', color: '#ccc', mr: 0.5 }} /> }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

// ========== Bottom Menu Item Row ==========
const BottomMenuItemRow: React.FC<{
  item: BottomMenuItem;
  index: number;
  total: number;
  onChange: (id: string, field: string, value: any) => void;
  onToggle: (id: string, enabled: boolean) => void;
  onMove: (index: number, dir: 'up' | 'down') => void;
  onDelete: (id: string) => void;
}> = ({ item, index, total, onChange, onToggle, onMove, onDelete }) => {

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${item.enabled ? '#c8e6c9' : '#e0e0e0'}`,
        borderRadius: 2,
        p: 2,
        mb: 1,
        bgcolor: item.enabled ? '#f1f8e9' : '#fafafa',
        opacity: item.enabled ? 1 : 0.6,
        transition: 'all 0.2s ease'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Avatar sx={{ width: 28, height: 28, bgcolor: '#e0e0e0' }}>
          {getIconComponent(item.icon, '#555', 'small')}
        </Avatar>
        <Typography fontWeight={500} sx={{ flex: 1, fontSize: '0.85rem' }}>{item.title}</Typography>
        <Tooltip title="نقل لأعلى"><span>
          <IconButton size="small" disabled={index === 0} onClick={() => onMove(index, 'up')}><UpIcon fontSize="small" /></IconButton>
        </span></Tooltip>
        <Tooltip title="نقل لأسفل"><span>
          <IconButton size="small" disabled={index === total - 1} onClick={() => onMove(index, 'down')}><DownIcon fontSize="small" /></IconButton>
        </span></Tooltip>
        <Switch size="small" checked={item.enabled} onChange={(e) => onToggle(item.id, e.target.checked)} />
        <IconButton size="small" color="error" onClick={() => onDelete(item.id)}><DeleteIcon fontSize="small" /></IconButton>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <TextField size="small" fullWidth label="العنوان" value={item.title} disabled={!item.enabled} onChange={(e) => onChange(item.id, 'title', e.target.value)} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>الأيقونة</InputLabel>
            <Select value={item.icon} label="الأيقونة" disabled={!item.enabled} onChange={(e) => onChange(item.id, 'icon', e.target.value)}>
              {iconOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField size="small" fullWidth label="الرابط" value={item.path} disabled={!item.enabled} onChange={(e) => onChange(item.id, 'path', e.target.value)} />
        </Grid>
      </Grid>
    </Paper>
  );
};

// ========== Main Component ==========
interface Props {
  settings: DisplaySettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

const MobileSidebarManager: React.FC<Props> = ({ settings, onSave, onError, onSuccess }) => {
  const theme = useTheme();
  const { updateSection, settings: storeSettings } = useStoreSettings();
  const [mainItems, setMainItems] = useState<MainMenuItem[]>(settings.mobileMainMenuItems || defaultMainMenuItems);
  const [bottomItems, setBottomItems] = useState<BottomMenuItem[]>(settings.mobileBottomMenuItems || defaultBottomMenuItems);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setMainItems(settings.mobileMainMenuItems || defaultMainMenuItems);
    setBottomItems(settings.mobileBottomMenuItems || defaultBottomMenuItems);
  }, [settings]);

  // ===== Main Items Handlers =====
  const handleMainChange = (id: string, field: string, value: any) => {
    setMainItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };
  const handleMainToggle = (id: string, enabled: boolean) => {
    setMainItems(prev => prev.map(item => item.id === id ? { ...item, enabled } : item));
  };
  const handleMainMove = (index: number, dir: 'up' | 'down') => {
    const next = [...mainItems];
    const target = dir === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setMainItems(next);
  };
  const handleMainDelete = (id: string) => setMainItems(prev => prev.filter(item => item.id !== id));
  const handleMainAdd = () => {
    const newItem: MainMenuItem = {
      id: `custom_${Date.now()}`,
      title: 'عنصر جديد',
      icon: 'home',
      path: '/',
      enabled: true,
      color: '#0d51d6'
    };
    setMainItems(prev => [...prev, newItem]);
  };

  // ===== Bottom Items Handlers =====
  const handleBottomChange = (id: string, field: string, value: any) => {
    setBottomItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };
  const handleBottomToggle = (id: string, enabled: boolean) => {
    setBottomItems(prev => prev.map(item => item.id === id ? { ...item, enabled } : item));
  };
  const handleBottomMove = (index: number, dir: 'up' | 'down') => {
    const next = [...bottomItems];
    const target = dir === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setBottomItems(next);
  };
  const handleBottomDelete = (id: string) => setBottomItems(prev => prev.filter(item => item.id !== id));
  const handleBottomAdd = () => {
    const newItem: BottomMenuItem = {
      id: `custom_${Date.now()}`,
      title: 'عنصر جديد',
      icon: 'info',
      path: '/about',
      enabled: true
    };
    setBottomItems(prev => [...prev, newItem]);
  };

  // ===== Save =====
  const handleSave = async () => {
    try {
      setSaving(true);
      onSave(true);
      onError(null);
      await updateSection('display', {
        ...settings,
        mobileMainMenuItems: mainItems,
        mobileBottomMenuItems: bottomItems
      });
      setSaved(true);
      onSuccess(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving sidebar settings:', err);
      onError('حدث خطأ أثناء حفظ إعدادات القائمة الجانبية');
    } finally {
      setSaving(false);
      onSave(false);
    }
  };

  const storeName = storeSettings?.general?.storeName || 'المتجر';

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        mb: 3, flexWrap: 'wrap', gap: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            p: 1, borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          }}>
            <PhoneAndroidIcon sx={{ color: '#fff', fontSize: '1.4rem' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">إدارة القائمة الجانبية للموبايل</Typography>
            <Typography variant="caption" color="text.secondary">
              تحكم كامل في عناصر القائمة — العناوين، الأيقونات، الألوان، والروابط
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            px: 3, borderRadius: 2, fontWeight: 'bold'
          }}
        >
          {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
        </Button>
      </Box>

      {saved && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>
          ✅ تم حفظ إعدادات القائمة الجانبية بنجاح! التغييرات ستظهر فوراً.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: Editor */}
        <Grid item xs={12} lg={8}>
          {/* Section 1: Main Menu Items (The Yellow highlighted ones) */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 4, height: 28, bgcolor: theme.palette.primary.main, borderRadius: 2 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">القائمة الرئيسية (العلوية)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    العناصر المميزة بالأصفر في الصورة — العروض، الأكثر مبيعاً، المنتجات المميزة...
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                size="small"
                onClick={handleMainAdd}
                sx={{ borderRadius: 2 }}
              >
                إضافة عنصر
              </Button>
            </Box>

            {mainItems.map((item, index) => (
              <MainMenuItemRow
                key={item.id}
                item={item}
                index={index}
                total={mainItems.length}
                onChange={handleMainChange}
                onToggle={handleMainToggle}
                onMove={handleMainMove}
                onDelete={handleMainDelete}
              />
            ))}

            {mainItems.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <Typography>لا توجد عناصر. اضغط "إضافة عنصر" لإضافة واحد.</Typography>
              </Box>
            )}
          </Paper>

          {/* Section 2: Bottom Menu Items */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 4, height: 28, bgcolor: '#4caf50', borderRadius: 2 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">القائمة الفرعية (السفلية)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    العناصر التي تظهر أسفل الأقسام — حول المتجر، اتصل بنا...
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                size="small"
                color="success"
                onClick={handleBottomAdd}
                sx={{ borderRadius: 2 }}
              >
                إضافة عنصر
              </Button>
            </Box>

            {bottomItems.map((item, index) => (
              <BottomMenuItemRow
                key={item.id}
                item={item}
                index={index}
                total={bottomItems.length}
                onChange={handleBottomChange}
                onToggle={handleBottomToggle}
                onMove={handleBottomMove}
                onDelete={handleBottomDelete}
              />
            ))}

            {bottomItems.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <Typography>لا توجد عناصر فرعية.</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right: Live Preview */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ position: 'sticky', top: 80 }}>
            <Paper elevation={2} sx={{ p: 2.5, borderRadius: 3, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                <PreviewIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">معاينة حية</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                هذا ما سيراه المستخدم في القائمة الجانبية
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <MobileSidebarPreview
                  mainItems={mainItems}
                  bottomItems={bottomItems}
                  storeName={storeName}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary">
                📝 التغييرات تظهر فوراً في المعاينة
              </Typography>
            </Paper>

            {/* Quick guide */}
            <Paper elevation={1} sx={{ p: 2, mt: 2, borderRadius: 3, bgcolor: alpha(theme.palette.info.main, 0.05), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
              <Typography variant="caption" fontWeight="bold" color="info.main" sx={{ display: 'block', mb: 1 }}>
                💡 دليل سريع
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, '& li': { fontSize: '0.72rem', color: 'text.secondary', mb: 0.5 } }}>
                <li>استخدم <strong>السهمين</strong> لإعادة ترتيب العناصر</li>
                <li>استخدم <strong>المفتاح</strong> لإخفاء/إظهار العنصر</li>
                <li>اكتب كود لون مثل <code>#f44336</code> لتغيير اللون</li>
                <li>الشارة (badge) تظهر بجانب العنصر مثل "جديد"</li>
                <li>الرابط هو المسار في الموقع مثل <code>/offers</code></li>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Save button at bottom */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => {
            setMainItems(settings.mobileMainMenuItems || defaultMainMenuItems);
            setBottomItems(settings.mobileBottomMenuItems || defaultBottomMenuItems);
          }}
        >
          تراجع عن التغييرات
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="large"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            px: 4, borderRadius: 2, fontWeight: 'bold'
          }}
        >
          {saving ? 'جاري الحفظ...' : '💾 حفظ إعدادات القائمة الجانبية'}
        </Button>
      </Box>
    </Box>
  );
};

export default MobileSidebarManager;
